import { Component } from 'react'
import { extendObservable } from 'mobx'
import { observer } from 'mobx-react'

import * as h from 'h'

import { parseColor, convertHex, urlEncodeName } from 'utils'
import { projectTypeBrands } from 'config'

import ProjectModel from 'models/ProjectModel'
import ResourceModel from 'models/ResourceModel'

import styles from './styles.scss'

// in the interest of keeping the result box generic,
// here we have adapters that match an instance of a model
// then create the needed "parts" of the result box
// based on that particular instance, so that ResultBox
// doesn't really need to know anything about what
// a project needs, or what a company needs, etc.
// the result will be merged into the defaults (below)

const defaults = {
  title: '',
  indicatorBar: null,
  contact: null,
  image: null,
  stripped: false
}

const translucentProjectTypeColors = Object.keys(projectTypeBrands)
  .reduce((acc, brand) => {
    acc[brand] = convertHex(parseColor(projectTypeBrands[brand]))
    return acc
  }, {})

const adapters = [
  [
    ProjectModel,
    project => {
      const { name, image, type, subtype, contactName } = project

      const isCompany = project.type && project.type.slug === 'companies'
      const isIntellectualProperty = project.type && project.type.slug === 'ip'

      const finalImageUrl = image && (image.tile || image.main || image.details)
      const finalImage = finalImageUrl && ({
        url: finalImageUrl,
        width: image.width,
        height: image.height,
        noCover: isCompany
      })

      const iBMessage = isIntellectualProperty
        ? project.description
        : subtype && subtype.name

      return {
        title: name,
        slug: type.slug,
        indicatorBar: {
          message: iBMessage,
          color: translucentProjectTypeColors[type && type.slug] ||
                 'rgba(255,255,255,.6)'
        },
        contact: {
          name: contactName,
          email: null // TODO obtain real email instead of (old) faked
        },
        image: finalImage,
        stripped: isCompany
      }
    }
  ],
  [
    ResourceModel,
    resource => {
      const { image } = resource

      const finalImageUrl = image && (image.tile || image.main || image.details)
      const finalImage = finalImageUrl && ({
        url: finalImageUrl,
        width: image.width,
        height: image.height,
        noCover: false
      })

      return {
        title: resource.name,
        slug: 'resource',
        image: finalImage,
        stripped: false
      }
    }
  ]
]

// Sub-component
const ContactBar = ({ contact, flip }) =>
  contact && (
    h.div(null,
      h.z({ onClick: flip, ...h.ca(styles.barLink) },
        h.icon({ name: 'user' })),
      h.div(h.ca(styles.bar),
        h.span(null,
          h.strong(null, 'Contact'),
          h.tsep(),
          contact.name || 'Not Available',
          h.maybe(contact.email,
            h.span(null, h.tsep(), contact.email))))))

// Sub-component
const IndicatorBar = ({ ib }) =>
  ib && (
    h.div({ style: { backgroundColor: parseColor(ib.color) }, ...h.ca(styles.indicatorBar) },
      h.span(h.ca(styles.indicatorBarMessage),
        ib.message)))

// Main component
@observer
export default class ResultBox extends Component {
  // --------------------------------------------------------------------------
  // Lifecycle
  // --------------------------------------------------------------------------

  componentWillMount () {
    extendObservable(this, {
      showBar: false
    })
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  flip = () => {
    this.showBar = !this.showBar
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  adapt (element) {
    let finalAdapter = () => ({}) // a noop basically, returning empty object

    // find the appropriate adapter for this element
    for (let matcher of adapters) {
      const [ model, adapter ] = matcher

      if (element instanceof model) {
        finalAdapter = adapter
        break
      }
    }

    // apply adapter to element
    const adapted = finalAdapter(this.props.element)

    // merge into defaults
    return Object.assign({}, defaults, adapted)
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  render () {
    const { small, element } = this.props

    const adapted = this.adapt(element)
    const { image } = adapted

    const classes = [
      styles.resultBox,
      {
        [styles.showBar]: this.showBar,
        [styles.small]: small
      }
    ]

    const imageClasses = image && [
      styles.image,
      { [styles.noCover]: image.noCover }
    ]

    // Only ID used to fetch element; name at end just for prettiness.
    const sharableLink = `/${adapted.slug}/${element.id}/${urlEncodeName(element.name)}`

    return h.div(h.ca(classes),
      h.h(ContactBar, { contact: adapted.contact, flip: this.flip }),
      h.l({ to: sharableLink, ...h.ca(styles.boxLink) },
        h.maybe(adapted.image,
          h.div({ style: { backgroundImage: `url(${adapted.image.url})` }, ...h.ca(imageClasses) })),
        h.div(h.ca(styles.overlay, { [styles.overlayStripped]: adapted.stripped }),
          h.maybe(!adapted.stripped,
            h.div(h.ca(styles.titleWrapper),
              h.div(h.ca(styles.title),
                adapted.title))),
          h.h(IndicatorBar, { ib: adapted.indicatorBar }))))
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/ResultBox/index.js